import React, { useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { useAppState } from '@sg/garnish';

import { Bag } from '@order/features/ordering';
import { StripePaymentProvider } from '@order/integrations/stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { getEnvVars } from '@order/utils';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useCloseBag } from '../../hooks';
import { BagAnimatedDialog, BagContent } from './components';
import { useBagGestures, useOrderState } from './hooks';
import { getOrderPlaybackConfig } from './utils';

export const BagScreen = () => {
  const orderPlaybackConfigFlag = useFeatureFlag('CELS-4127-order-playback-config'); // prettier-ignore
  const isOrderPlaybackRedesignEnabledFlag = useFeatureFlag('CELS-4719-order-playback-redesign-enabled'); // prettier-ignore

  const { orderSubmissionDelay } = getOrderPlaybackConfig(
    orderPlaybackConfigFlag,
    isOrderPlaybackRedesignEnabledFlag,
  );

  const orderState = useOrderState({ orderSubmissionDelay });

  const {
    isFetchingInitialData,
    isSubmittingOrder,
    isShowingModal,
    refetchWantedTimes,
    startOrdering,
    purchaseAttempt,
    stripeSession,
    total,
  } = orderState;

  const isLoggedIn = useIsLoggedIn();
  const shouldRenderAnimatedDialog = isLoggedIn && !isFetchingInitialData;

  // ──────────────────────────────────────────────────────────────────────────

  const closeBag = useCloseBag();

  // Prevent closing bag while submitting an order.
  const handleCloseBag = useCallback(() => {
    if (isSubmittingOrder) return;

    closeBag();
  }, [isSubmittingOrder, closeBag]);

  // Enable/disable gestures when modal is shown.
  useBagGestures(isShowingModal);

  // Fetch ordering data on bag focus.
  useFocusEffect(startOrdering);

  // Refetch wanted times on app focus.
  useAppState({ onVisible: refetchWantedTimes });

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Bag.DynamicModal onClose={handleCloseBag}>
      <BagContent orderState={orderState} />

      {shouldRenderAnimatedDialog ? (
        <StripePaymentProvider
          publicKey={stripePublicKey}
          purchaseAttempt={purchaseAttempt}
          sessionId={stripeSession}
          amount={total}
        >
          <BagAnimatedDialog orderState={orderState} />
        </StripePaymentProvider>
      ) : null}
    </Bag.DynamicModal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const stripePublicKey = getEnvVars().STRIPE_PUBLIC_KEY as string;
